import { useState, useCallback } from 'react';

export interface UseModalReturn {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Open the modal */
  open: () => void;
  /** Close the modal */
  close: () => void;
  /** Toggle the modal open/closed state */
  toggle: () => void;
}

/**
 * Hook for managing modal open/close state.
 * 
 * @param initialState - Initial open state (default: false)
 * @returns Object with isOpen state and open/close/toggle callbacks
 * 
 * @example
 * ```tsx
 * const loginModal = useModal();
 * 
 * return (
 *   <>
 *     <Button onClick={loginModal.open}>Login</Button>
 *     <LoginModal show={loginModal.isOpen} onHide={loginModal.close} />
 *   </>
 * );
 * ```
 */
export function useModal(initialState = false): UseModalReturn {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  return { isOpen, open, close, toggle };
}

export default useModal;
